import { useCallback, useEffect, useMemo, useState } from "react";
import AdminHeader from "../components/admin/AdminHeader";
import { Link } from "react-router-dom";
import {
  ArrowLeft, ShieldCheck, Loader2, Plus, Trash2, CalendarRange,
  CheckCircle2, Circle, Eye, ExternalLink,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../components/ui/dialog";
import { adminApi, useAdminAuth } from "../lib/adminAuth";
import FdaLabelPanel from "../components/admin/FdaLabelPanel";
import SourceChips from "../components/admin/SourceChips";

const SLOT_HINTS = {
  1: "What happens in the first days. What to stop now.",
  2: "When side effects are strongest. What's normal, what's not.",
  3: "What's improving. What to keep doing.",
  4: "Last weeks. When to restart actives. What to ask the prescriber.",
};

function slotSources(s) {
  return s.sources || [];
}

function isTouched(s) {
  return s.week != null || !!(s.body || "").trim() || slotSources(s).length > 0
    || ((s.title || "").trim() !== (s.slot_name || "").trim() && !!(s.title || "").trim());
}

function isComplete(s) {
  return s.week != null && !!(s.body || "").trim() && slotSources(s).length > 0;
}

function slotStatus(s) {
  if (isComplete(s)) return { tag: "complete", tone: "text-emerald-700" };
  if (!isTouched(s)) return { tag: "empty — won't send", tone: "text-stone-400" };
  if (slotSources(s).length === 0) return { tag: "needs source", tone: "text-red-600" };
  if (!(s.body || "").trim()) return { tag: "needs body", tone: "text-red-600" };
  return { tag: "needs week", tone: "text-red-600" };
}

function slotLabel(s, idx) {
  if (s.slot) return `${s.slot} · ${s.slot_name}`;
  return `Week ${s.week ?? "?"}`;
}

function missingBit(s) {
  if (slotSources(s).length === 0) return "source";
  if (!(s.body || "").trim()) return "body";
  if (s.week == null) return "week number";
  return null;
}

function SlotCard({ s, idx, active, onFocus, onChange, onRemove }) {
  const st = slotStatus(s);
  const complete = st.tag === "complete";
  return (
    <div
      data-testid={`slot-card-${s._k}`}
      onFocusCapture={onFocus}
      className={`rounded-xl border p-3 transition ${
        complete ? "border-emerald-400 bg-emerald-50/30"
          : active ? "border-stone-900" : "border-stone-200 bg-white"}`}
    >
      <div className="flex items-center justify-between">
        <b className="text-sm text-stone-900">{slotLabel(s, idx)}</b>
        <div className="flex items-center gap-2">
          <span className={`text-[11px] font-medium ${st.tone}`} data-testid={`slot-status-${s._k}`}>
            {st.tag}
          </span>
          {!s.slot && (
            <button className="text-red-400 hover:text-red-600" onClick={onRemove}
                    data-testid={`slot-remove-${s._k}`}>
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>

      <div className="mt-2 flex gap-2">
        <Input
          type="number" min={1} value={s.week ?? ""} placeholder="Wk"
          onChange={(e) => onChange({ ...s, week: e.target.value === "" ? null : parseInt(e.target.value, 10) })}
          className="h-9 w-16 text-sm" data-testid={`slot-week-${s._k}`}
        />
        <Input
          value={s.title} placeholder={s.slot_name || "Week title"}
          onChange={(e) => onChange({ ...s, title: e.target.value })}
          className="h-9 flex-1 text-sm" data-testid={`slot-title-${s._k}`}
        />
      </div>

      <Textarea
        value={s.body} rows={3} maxLength={600}
        placeholder={SLOT_HINTS[s.slot] || "What they'll feel and when · what to do"}
        onChange={(e) => onChange({ ...s, body: e.target.value })}
        className="mt-2 text-sm" data-testid={`slot-body-${s._k}`}
      />
      <div className="mt-0.5 text-right text-[10px] text-stone-400">{(s.body || "").length}/600</div>

      <div className="mt-1" data-testid={`slot-sources-row-${s._k}`}>
        <SourceChips
          sources={slotSources(s)}
          testid={`slot-sources-${s._k}`}
          onChange={(srcs) => onChange({ ...s, sources: srcs })}
        />
      </div>
    </div>
  );
}

function PatientPreview({ open, onOpenChange, medName, slot }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="patient-preview-modal">
        <DialogHeader>
          <DialogTitle>Preview as patient</DialogTitle>
        </DialogHeader>
        {!slot ? (
          <p className="text-sm text-stone-500">
            No finished slot yet. Complete a slot (week + body + source) to preview it.
          </p>
        ) : (
          <div className="space-y-4">
            <p className="text-[11px] uppercase tracking-wider text-stone-400">
              As someone who started today would see it · nothing is sent
            </p>

            <div>
              <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-stone-500">Dashboard card</p>
              <div className="rounded-2xl border border-stone-200 bg-white p-5 shadow-sm" data-testid="preview-dashboard-card">
                <div className="flex items-center justify-between">
                  <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-stone-400">{medName}</div>
                  <span className="rounded-full border border-stone-200 bg-stone-50 px-2 py-0.5 text-[11px] font-semibold text-stone-600">
                    Week {slot.week}
                  </span>
                </div>
                <div className="mt-2 text-lg font-extrabold tracking-tight text-stone-900">
                  Week {slot.week}: {slot.title}
                </div>
                <p className="mt-1 text-[14px] leading-relaxed text-stone-600">{slot.body}</p>
                <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1">
                  {(slot.sources || []).map((s, i) => (
                    <a key={i} href={s.url} target="_blank" rel="noreferrer"
                       className="inline-flex items-center gap-1 text-[12px] text-emerald-700 hover:underline"
                       data-testid={`preview-source-${i}`}>
                      {s.label} <ExternalLink className="h-3 w-3" />
                    </a>
                  ))}
                </div>
              </div>
            </div>

            <div>
              <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-stone-500">Week-1 email</p>
              <div className="overflow-hidden rounded-xl border border-stone-200" data-testid="preview-email-card">
                <div className="border-b border-stone-100 bg-stone-50 px-4 py-2 text-[12px] text-stone-500">
                  Subject: <b className="text-stone-700">Week {slot.week} on {medName}: {slot.title}</b>
                </div>
                <div className="bg-white p-4">
                  <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-stone-400">BlendSafe</div>
                  <h3 className="mt-1 text-[17px] font-semibold text-stone-900">Week {slot.week} on {medName}</h3>
                  <div className="mt-3 rounded-xl border border-stone-200 bg-[#fbf6ee] p-4">
                    <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-stone-400">Week {slot.week}</div>
                    <div className="mt-0.5 text-[16px] font-semibold text-stone-900">{slot.title}</div>
                    <p className="mt-1.5 text-[14px] leading-relaxed text-stone-600">{slot.body}</p>
                    <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1">
                      {(slot.sources || []).map((s, i) => (
                        <a key={i} href={s.url} target="_blank" rel="noreferrer"
                           className="inline-block text-[12px] text-emerald-700 hover:underline">{s.label}</a>
                      ))}
                    </div>
                  </div>
                  <div className="mt-3 inline-block rounded-lg bg-stone-900 px-4 py-2 text-[13px] font-semibold text-[#FAF7F2]">
                    See your tracker
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function CourseEditor({ course, onBack, onSaved, onDeleted }) {
  const { admin } = useAdminAuth();
  const canReview = admin?.role === "clinician";
  const [slots, setSlots] = useState(
    (course.weeks || []).map((w, i) => ({ ...w, _k: `${course.id}-${w.slot ?? "x"}-${i}` })),
  );
  const [reviewedBy, setReviewedBy] = useState(course.reviewed_by || "");
  const [approved, setApproved] = useState(course.approved);
  const [activeKey, setActiveKey] = useState(slots[0]?._k || null);
  const [busy, setBusy] = useState("");
  const [preview, setPreview] = useState(false);

  const blockers = useMemo(() => {
    const out = [];
    slots.forEach((s) => {
      if (isTouched(s) && !isComplete(s)) {
        const m = missingBit(s);
        if (m) out.push(`${s.slot ? `Slot ${s.slot}` : `Week ${s.week ?? "?"}`} ${m}`);
      }
    });
    if (!slots.some(isComplete)) out.push("at least one finished slot");
    return out;
  }, [slots]);

  const canApprove = blockers.length === 0;

  const previewSlot = useMemo(() => {
    const done = slots.filter(isComplete).sort((a, b) => (a.week || 0) - (b.week || 0));
    return done[0] || null;
  }, [slots]);

  function payload(extra = {}) {
    return {
      weeks: slots.map(({ _k, ...w }) => ({
        ...w, week: w.week == null ? null : Number(w.week),
      })),
      ...extra,
    };
  }

  async function save(extra, label) {
    const key = extra.approved === true ? "approve" : extra.approved === false ? "unapprove" : "save";
    setBusy(key);
    try {
      const { data } = await adminApi().put(`/admin/courses/${course.id}`, payload(extra));
      setApproved(data.approved);
      setReviewedBy(data.reviewed_by || "");
      onSaved(data);
      toast.success(label || "Saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(""); }
  }

  async function remove() {
    if (!window.confirm(`Delete the ${course.medication_name} timeline?`)) return;
    setBusy("delete");
    try {
      await adminApi().delete(`/admin/courses/${course.id}`);
      onDeleted(course.id);
      toast.success("Deleted");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
      setBusy("");
    }
  }

  function addWeek() {
    const nums = slots.map((s) => s.week || 0);
    const next = nums.length ? Math.max(...nums) + 1 : 1;
    setSlots([...slots, {
      _k: `${course.id}-extra-${Date.now()}`, slot: null, slot_name: null,
      week: next, title: "", body: "", sources: [],
    }]);
  }

  function fillActiveSource(src) {
    if (!activeKey) { toast.message("Click a slot first, then Use"); return false; }
    const cur = slots.find((s) => s._k === activeKey)?.sources || [];
    if (cur.some((x) => x.url === src.url)) return false;
    setSlots((prev) => prev.map((s) => (s._k === activeKey
      ? { ...s, sources: [...(s.sources || []), { url: src.url, label: src.label }] } : s)));
    return true;
  }

  const activeSlot = slots.find((s) => s._k === activeKey);
  const useHint = activeSlot
    ? (activeSlot.slot ? `Slot ${activeSlot.slot} · ${activeSlot.slot_name}` : `Week ${activeSlot.week ?? "?"}`)
    : null;

  return (
    <div data-testid={`course-editor-${course.id}`}>
      {/* Top bar */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <button onClick={onBack} className="text-sm text-stone-500 hover:text-stone-900"
                  data-testid="editor-back">← All timelines</button>
          <span className="text-lg font-semibold text-stone-900">{course.medication_name}</span>
          <span className={`inline-flex items-center gap-1 rounded-lg px-2.5 py-0.5 text-[11px] font-medium ${
            approved ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}
            data-testid="editor-status-badge">
            {approved ? <CheckCircle2 className="h-3 w-3" /> : <Circle className="h-3 w-3" />}
            {approved ? "Approved" : "Draft"}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={() => setPreview(true)} data-testid="editor-preview">
            <Eye className="h-3.5 w-3.5" /><span className="ml-1">Preview as patient</span>
          </Button>
          <Button size="sm" variant="outline" onClick={() => save({}, "Saved")} disabled={!!busy}
                  data-testid="editor-save">
            {busy === "save" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Save"}
          </Button>
          {approved ? (
            <Button size="sm" variant="outline" onClick={() => save({ approved: false }, "Moved back to draft")}
                    disabled={!!busy || !canReview}
                    title={!canReview ? "Clinical reviewer only." : undefined}
                    data-testid="editor-unapprove">Unapprove</Button>
          ) : (
            <Button size="sm" onClick={() => save({ approved: true }, "Approved — live for users")}
                    disabled={!!busy || !canApprove || !canReview}
                    title={!canReview ? "Clinical reviewer only." : undefined}
                    data-testid="editor-approve">
              {busy === "approve" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Approve"}
            </Button>
          )}
          <Button size="sm" variant="ghost" className="text-red-600 hover:bg-red-50"
                  onClick={remove} disabled={!!busy} data-testid="editor-delete">
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      {!approved && (!canReview ? (
        <div className="mt-2 text-[13px] text-stone-500" data-testid="approve-needs">
          Clinical reviewer only — Meghana approves timelines.
        </div>
      ) : blockers.length > 0 && (
        <div className="mt-2 text-[13px] text-red-600" data-testid="approve-needs">
          Approve needs: {blockers.join(" · ")}
        </div>
      ))}

      <div className="mt-4 grid gap-4 lg:grid-cols-[3fr_2fr]">
        {/* Left: timeline */}
        <div className="flex flex-col gap-3">
          <p className="text-[12px] text-stone-500">
            Plain words · 3–5 sentences · what they'll feel and when · what to do ·
            every sentence needs a section from the panel.
          </p>
          {slots.map((s, idx) => (
            <SlotCard
              key={s._k} s={s} idx={idx} active={activeKey === s._k}
              onFocus={() => setActiveKey(s._k)}
              onChange={(ns) => setSlots((prev) => prev.map((x) => (x._k === s._k ? { ...ns, _k: s._k } : x)))}
              onRemove={() => setSlots((prev) => prev.filter((x) => x._k !== s._k))}
            />
          ))}
          <Button size="sm" variant="outline" className="self-start" onClick={addWeek}
                  data-testid="editor-add-week">
            <Plus className="h-3.5 w-3.5" /><span className="ml-1">Add week</span>
          </Button>

          <div className="mt-2 text-[12px] text-stone-500" data-testid="editor-reviewer">
            {approved && reviewedBy
              ? <>Reviewed by <b className="text-stone-700">{reviewedBy}</b></>
              : "Reviewed by the clinical reviewer on approve."}
          </div>
        </div>

        {/* Right: FDA label panel */}
        <div>
          <FdaLabelPanel
            medicationId={course.medication_id}
            medicationName={course.medication_name}
            useHint={useHint}
            onUse={fillActiveSource}
          />
        </div>
      </div>

      <PatientPreview open={preview} onOpenChange={setPreview}
                      medName={course.medication_name} slot={previewSlot} />
    </div>
  );
}

export default function AdminCourses() {
  const [items, setItems] = useState([]);
  const [meds, setMeds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newMed, setNewMed] = useState("");
  const [creating, setCreating] = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, m] = await Promise.all([
        adminApi().get("/admin/courses"),
        adminApi().get("/admin/courses/medications"),
      ]);
      setItems(c.data.items);
      setMeds(m.data.items);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load courses");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function create() {
    if (!newMed) return;
    setCreating(true);
    try {
      const { data } = await adminApi().post("/admin/courses", { medication_id: newMed });
      setItems((prev) => (prev.some((i) => i.id === data.id) ? prev : [data, ...prev]));
      setNewMed("");
      setSelectedId(data.id);
      toast.success("Timeline created — four slots ready");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Create failed");
    } finally { setCreating(false); }
  }

  const existingMedIds = new Set(items.map((i) => i.medication_id));
  const available = meds.filter((m) => !existingMedIds.has(m.id));
  const selected = items.find((i) => i.id === selectedId);

  return (
    <main data-testid="admin-courses-page" className="min-h-screen bg-[#fdf8f3] px-6 py-10">
      <div className="mx-auto max-w-5xl">
        <AdminHeader />

        {selected ? (
          <CourseEditor
            key={selected.id}
            course={selected}
            onBack={() => setSelectedId(null)}
            onSaved={(u) => setItems((prev) => prev.map((i) => (i.id === u.id ? { ...i, ...u } : i)))}
            onDeleted={(id) => { setItems((prev) => prev.filter((i) => i.id !== id)); setSelectedId(null); }}
          />
        ) : (
          <>
            <div className="flex items-center gap-2">
              <CalendarRange className="h-6 w-6 text-stone-700" />
              <h1 className="text-3xl font-semibold tracking-tight text-stone-900">Course Tracker</h1>
            </div>
            <p className="mt-1 text-sm text-stone-600">
              Each timeline opens with four fixed slots. Fill a slot's week, body and a
              source from the FDA label panel; only complete slots reach users. A timeline
              goes live once <strong>approved</strong>.
            </p>

            <div className="mt-5 flex flex-wrap items-end gap-2 rounded-xl border border-dashed border-stone-300 bg-white p-4">
              <div className="min-w-[220px] flex-1">
                <label className="text-[11px] font-medium uppercase tracking-wide text-stone-500">Add a timeline for</label>
                <Select value={newMed} onValueChange={setNewMed}>
                  <SelectTrigger data-testid="course-new-med" className="mt-1 h-9 text-sm"><SelectValue placeholder="Choose a medication…" /></SelectTrigger>
                  <SelectContent className="max-h-72">
                    {available.map((m) => (
                      <SelectItem key={m.id} value={m.id} className="text-sm">{m.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={create} disabled={creating || !newMed} data-testid="course-create">
                {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                <span className="ml-1">Create</span>
              </Button>
            </div>

            {loading ? (
              <div className="mt-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-stone-400" /></div>
            ) : (
              <div className="mt-5 space-y-2">
                {items.map((c) => (
                  <button
                    key={c.id} onClick={() => setSelectedId(c.id)}
                    data-testid={`course-row-${c.id}`}
                    className="flex w-full items-center justify-between rounded-xl border border-stone-200 bg-white px-4 py-3 text-left transition hover:border-stone-900"
                  >
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-stone-900">{c.medication_name}</span>
                      <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide ${
                        c.approved ? "bg-emerald-100 text-emerald-800" : "bg-stone-100 text-stone-500"}`}>
                        {c.approved ? "Live" : "Draft"}
                      </span>
                    </div>
                    <span className="text-xs text-stone-500">{c.sendable_count} of {c.week_count} slots ready →</span>
                  </button>
                ))}
                {items.length === 0 && (
                  <p className="py-8 text-center text-sm text-stone-500">No timelines yet. Add one above.</p>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </main>
  );
}
